/**
 * The state a student's sitting is in, and how moving around it works.
 *
 * Kept apart from the screen so the rules can be tested: which questions count
 * as answered, which were skipped, and where next and previous land.
 */

export interface ExamQuestion {
  questionNumber: number;
  questionId: string;
  marks: number;
  question: any;
}

export interface ExamAttempt {
  id: string;
  attemptNo: number;
  status: 'in_progress' | 'submitted' | 'expired';
  startedAt: string;
  deadlineAt: string;
  remainingSeconds: number;
  totalQuestions: number;
  totalMarks: number;
  resumed: boolean;
}

export interface ExamPaper {
  id: string;
  title: string;
  paperNumber: number;
  durationMinutes: number;
  mode: 'paged' | 'single';
  warnAtMinutes: number[];
}

/** Answers held by question id, as the student has them on screen. */
export type AnswerMap = Record<string, string[]>;

export type QuestionState = 'answered' | 'skipped' | 'unseen';

/** A selection counts only if it holds something. An emptied answer is not one. */
export const hasSelection = (value: string[] | undefined): boolean =>
  Array.isArray(value) && value.some((entry) => `${entry ?? ''}`.trim() !== '');

/**
 * What the navigator shows for each question.
 *
 * Answered comes from the answers themselves. The difference between skipped
 * and unseen is whether the student has opened the question, which nothing on
 * the server knows, so it is carried here.
 */
export const questionStates = (
  questions: ExamQuestion[],
  answers: AnswerMap,
  seen: string[],
): QuestionState[] => {
  const opened = new Set(seen);

  return questions.map((question) => {
    if (hasSelection(answers[question.questionId])) {
      return 'answered';
    }
    return opened.has(question.questionId) ? 'skipped' : 'unseen';
  });
};

export const countStates = (states: QuestionState[]) => ({
  answered: states.filter((state) => state === 'answered').length,
  skipped: states.filter((state) => state === 'skipped').length,
  unseen: states.filter((state) => state === 'unseen').length,
});

/** How many questions carry no answer, which is what the submit warning counts. */
export const unansweredCount = (questions: ExamQuestion[], answers: AnswerMap): number =>
  questions.filter((question) => !hasSelection(answers[question.questionId])).length;

export const clampIndex = (index: number, length: number): number => {
  if (length <= 0) {
    return 0;
  }
  return Math.min(Math.max(index, 0), length - 1);
};

export const nextIndex = (index: number, length: number): number => clampIndex(index + 1, length);

export const previousIndex = (index: number, length: number): number =>
  clampIndex(index - 1, length);

export const isLast = (index: number, length: number): boolean => index >= length - 1;

/**
 * The first question still without an answer, which is where a resumed sitting
 * should open rather than back at question one.
 */
export const firstUnanswered = (questions: ExamQuestion[], answers: AnswerMap): number => {
  const index = questions.findIndex((question) => !hasSelection(answers[question.questionId]));
  return index === -1 ? 0 : index;
};

/** Turns the answers the server returned into the map the screen holds. */
export const toAnswerMap = (
  saved: { questionId: string; selectedAnswer: string[] }[] | undefined,
): AnswerMap => {
  const map: AnswerMap = {};
  (saved ?? []).forEach((answer) => {
    if (answer?.questionId) {
      map[answer.questionId] = answer.selectedAnswer ?? [];
    }
  });
  return map;
};

/** Where the opened-questions list is kept, so a refresh does not lose it. */
export const seenStorageKey = (attemptId: string): string => `cba.seen.${attemptId}`;

export const readSeen = (attemptId: string): string[] => {
  try {
    const raw = window.localStorage.getItem(seenStorageKey(attemptId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    // A blocked or full storage must not stop a student sitting the paper.
    return [];
  }
};

export const writeSeen = (attemptId: string, seen: string[]): void => {
  try {
    window.localStorage.setItem(seenStorageKey(attemptId), JSON.stringify(seen));
  } catch (e) {
    // Losing the skipped markers is a small cost; failing the paper is not.
  }
};

/**
 * What the confirmation says before a paper is submitted.
 *
 * A student who has left questions blank should be told how many, because
 * submitting is the last chance to go back to them.
 */
export const submitMessage = (unanswered: number): string => {
  if (unanswered === 0) {
    return 'You have answered every question. Once you submit, your answers are final and the paper is marked.';
  }

  return `${unanswered} question${unanswered === 1 ? ' is' : 's are'} still unanswered. Once you submit, your answers are final and the paper is marked.`;
};
